import { useQuery, useQueryClient } from "react-query"
import { useHistory, useParams } from "react-router-dom"
import { Button, ConfirmDialog, DialogView } from "../../components"
import { Loader } from "../../components/Loader"
import { useTranslation } from "react-i18next";
import { SHOW_ORDER_RETURNS } from "../../navigation/constants"
import { useEffect, useState } from "react"
import {
  cancelReturn,
  duplicateReturnedOrder,
  fetchReturn,
} from "../../services/returnServices"
import { StatusHighlighted } from '../../components/NewTable/StatusHighlighted';
import { RETURNS_STATUSES} from '../../components/NewTable/StatusHighlightedDefinitions';
import { OrderLineItem } from "../Orders/OrderLineItem"
import { getReturnTypeDefinition } from "./ReturnTypes"
import { getReturnNewOrderLabel } from "./ReturnNewOrderLabels"
import { GenerateReplenishmentLabel } from "../Replenishments/GenerateReplenishmentLabel"
import { aggregateReturnedUnits } from "./returnedUnitsAggregation"
import { ReturnProofVideos } from "./ReturnProofVideos"
import moment from "moment"

export const ShowReturnContainer = () => {
  const { i18n } = useTranslation();
  let { id } = useParams()
  const history = useHistory()
  const {warehouse_id} = useParams()
  const queryClient = useQueryClient()
  const { isLoading, isFetching, isError, error, data, refetch } = useQuery(
    "return_detail",
    () => fetchReturn(id)
  )

  const [returnedStockUnits, setReturnedStockUnits] = useState([])
  const [allReplenishmentProducts, setAllReplenishmentProducts] = useState([])
  const [openDialogLabel, setOpenDialogLabel] = useState(false)
  const [replenishmentInfo, setReplenishmentInfo] = useState(null)

  useEffect(() => {
    if (replenishmentInfo) {
      setOpenDialogLabel(true)
    }
  }, [replenishmentInfo])

  useEffect(() => {
    if (!data) {
      return
    }

    // For bulk returns, calculate both: this return's products AND all replenishment products
    if (data.bulk_return_session) {
      // Map return_products (this return's specific products)
      if (data.return_products) {
        const mappedProducts = data.return_products.map((product) => ({
          product: {
            id: product.product_id,
            name: product.name,
            bar_code: product.barcode,
            product_codes: product.skus?.map(sku => ({ sku })) || [],
            image_url: product.image_url,
          },
          quantity: product.quantity,
          damaged: false,
        }))
        setReturnedStockUnits(mappedProducts)
      }

      // Aggregate all returned_stock_units (all products from shared replenishment).
      // One row per (product, damaged) with a per-lot breakdown shown as chips.
      setAllReplenishmentProducts(aggregateReturnedUnits(data.returned_stock_units))
      return
    }

    // For regular returns, aggregate from returned_stock_units: one row per
    // (product, damaged) with a per-lot breakdown shown as chips.
    setReturnedStockUnits(aggregateReturnedUnits(data.returned_stock_units))
    setAllReplenishmentProducts([])
  }, [data])

  const showOrderDetail = (order) => {
    history.push(SHOW_ORDER_RETURNS.replace(":id", order.id).replace(':warehouse_id', warehouse_id))
  }

  // Per-lot chips inside a product row: code · expiration · count. A non-ACTIVE lot
  // (EXPIRED / HOLDED / DAMAGED) is red-tinted (status in the tooltip) so the operator
  // can tell an unhealthy lot from a healthy one without leaving the screen.
  const renderLotChips = (lots) => {
    if (!lots || lots.length === 0) return null
    return (
      <div className="mt-1 flex flex-wrap gap-1">
        {lots.map((lot) => {
          const inactive = Boolean(lot.status) && lot.status !== 'ACTIVE'
          return (
            <span
              key={lot.id ?? 'no-lot'}
              title={inactive ? lot.status : undefined}
              className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium text-white ${inactive ? 'bg-red-700' : 'bg-blue-800'}`}
            >
              {i18n.t('returns.lot_label')}: {lot.unique_code}
              {lot.expiration_date ? ` · ${moment(lot.expiration_date, "YYYY-MM-DD").format('DD/MM/Y')}` : ''}
              {lot.count != null ? ` ×${lot.count}` : ''}
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <>
      {isError ? (
        <>Error: {error.message}</>
      ) : (
        <div className="relative">
          <Loader show={isFetching} />
          <div className="">
            <dl>
              <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Número de orden retornada
                </dt>
                <dd
                  className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2  hover:underline cursor-pointer"
                  onClick={() => showOrderDetail(data?.returned_order)}
                >
                  {data?.returned_order?.order_number ?
                 ( <>
                    #{data?.returned_order?.order_number}
                  </>)
                  : 
                  <>
                    <i>{i18n.t('returns.unidentified')}</i>
                  </>
                  }
                </dd>
              </div>
              <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Tienda</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {data?.returned_order?.store.name || data?.store?.name}
                </dd>
              </div>
              <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Estado</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <StatusHighlighted
                    status={data?.status}
                    statuses={RETURNS_STATUSES}
                  />
                </dd>
              </div>
              <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Tipo de Retorno</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {getReturnTypeDefinition(data?.return_type).label}
                </dd>
              </div>
              {data?.bulk_return_session && (
                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">{i18n.t('list_active_replenishments.bulk_return_label')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {i18n.t('list_active_replenishments.bulk_id')}: {data.bulk_return_session.id}
                    </span>
                    {data.bulk_return_session.replenishment_id && (
                      <span className="ml-2 text-sm text-gray-500">
                        {i18n.t('list_active_replenishments.replenishment_id')}: {data.bulk_return_session.replenishment_id}
                      </span>
                    )}
                  </dd>
                </div>
              )}
              <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Fecha de retorno
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {data &&
                    new Intl.DateTimeFormat("es", {
                      year: "numeric",
                      month: "long",
                      day: "2-digit",
                      hour: "numeric",
                      minute: "numeric",
                    }).format(new Date(data?.created_at))}
                </dd>
              </div>
              <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Notas</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {data?.notes}
                </dd>
              </div>
              {data?.return_type != "SIMPLE_RETURN" && (
                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Orden creada por retorno
                  </dt>
                  {data?.created_order ? (
                    <dd
                      className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2  hover:underline cursor-pointer"
                      onClick={() => showOrderDetail(data?.created_order)}
                    >
                      #{data?.created_order.order_number}
                    </dd>
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <i>{getReturnNewOrderLabel(data?.return_type, data?.status)}</i>
                    </dd>
                  )}
                </div>
              )}
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Productos retornados
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {data?.status === "REQUESTED" ? (
                    <div>
                      <i>La orden no ha regresado.</i>
                    </div>
                  ) : (
                    <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                      {returnedStockUnits.map((stockUnitGroup) => (
                        <li
                          className="pl-3 pr-4 py-3 text-md"
                          key={
                            String(stockUnitGroup.product.id) +
                            (stockUnitGroup.damaged ? "D" : "")
                          }
                        >
                          <OrderLineItem orderLine={stockUnitGroup} />
                          {renderLotChips(stockUnitGroup.lots)}
                        </li>
                      ))}
                    </ul>
                  )}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">{i18n.t('returns.proof_videos.title')}</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <ReturnProofVideos returnId={id} />
                </dd>
              </div>
              {/* For bulk returns, show all products from the shared replenishment */}
              {data?.bulk_return_session && allReplenishmentProducts.length > 0 && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {i18n.t('list_active_replenishments.bulk_replenishment_products')}
                    <p className="text-xs text-gray-400 mt-1">
                      {i18n.t('list_active_replenishments.bulk_replenishment_products_description')}
                    </p>
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                      {allReplenishmentProducts.map((stockUnitGroup) => (
                        <li
                          className="pl-3 pr-4 py-3 text-md"
                          key={
                            "all-" + String(stockUnitGroup.product.id) +
                            (stockUnitGroup.damaged ? "D" : "")
                          }
                        >
                          <OrderLineItem orderLine={stockUnitGroup} />
                          {renderLotChips(stockUnitGroup.lots)}
                        </li>
                      ))}
                    </ul>
                  </dd>
                </div>
              )}
              <div className="pt-5 pb-5">
                <div className="flex justify-end">
                  {data?.bulk_return_session?.replenishment_id && (
                    <Button
                      type="secondary"
                      className="w-full"
                      onClick={() => {
                        setReplenishmentInfo({
                          id: data.bulk_return_session.replenishment_id,
                          store: data.returned_order?.store || data.store
                        })
                      }}
                    >
                      {i18n.t('list_active_replenishments.reprint_label')}
                    </Button>
                  )}
                  {data?.rma_file && (
                    <Button
                      type="secondary"
                      className="ml-3 w-full"
                      onClick={() => {
                        window.open(
                          process.env.REACT_APP_PLATFORM_URL +
                            data?.rma_file.path
                        )
                      }}
                    >
                      Ver RMA
                    </Button>
                  )}
                  {data?.returned_order?.return_label?.label_file && (
                    <Button
                      className="ml-3 w-full"
                      onClick={() => {
                        window.open(
                          process.env.REACT_APP_PLATFORM_URL +
                            data.returned_order?.return_label.label_file.path
                        )
                      }}
                    >
                      Ver Guía de Retorno
                    </Button>
                  )}
                </div>
              </div>
            </dl>
          </div>
        </div>
      )}
      
      <DialogView className="w-full" setOpen={setOpenDialogLabel} open={openDialogLabel}>
        <GenerateReplenishmentLabel
          replenishment_info={replenishmentInfo}
          onCancel={() => {
            setOpenDialogLabel(false)
          }}
          onSuccess={() => {
            setOpenDialogLabel(false)
          }}
        />
      </DialogView>
    </>
  )
}
